import React, { useState } from "react";
import { useNavigate } from "react-router";
import { NavigatorItem } from "common/components/Navigator/types";
import { currentOrgId as currentOrgIdSelector } from "common/selectors/org.selectors";
import { currentJobId as currentJobIdSelector } from "common/selectors/job.selectors";
import { useAppDispatch, useAppSelector } from "common/hooks/storeHooks";
import { currentUserId, currentUserOrgId } from "common/selectors/user.selectors";
import { useNavigatorItems } from "common/components/Navigator/useNavigatorItems";
import { List } from "immutable";
import { OrgAsyncActions } from "common/reducers/org.async.actions";
import { orgParamsFromJSON } from "common/records/index/OrgParams";
import { LoadingBar } from "common/components/Navigator/LoadingBar";
import { loading } from "common/selectors/idsManagement.selectors";
import { AccessAsyncActions } from "common/reducers/access.async.actions";
import { accessParamsFromJSON } from "common/records/index/AccessParams";
import { AccessResult } from "common/components/Navigator/AccessResult";
import { UserActions } from "common/reducers/user.actions";
import { AuthAsyncActions } from "common/reducers/auth.async.actions";
import { debounce } from "common/helpers/Async";
import { JobAsyncActions } from "common/reducers/job.async.actions";
import { jobParamsFromJSON } from "common/records/index/JobParams";
import { OrgItemLabel } from "common/components/Navigator/OrgItemLabel";
import { JobActions } from "common/reducers/job.actions";

export const Navigator: React.FC<{ close: () => void }> = ({ close }) => {
  // Local State
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchingKind, setSearchingKind] = useState<"org" | "job" | "access" | undefined>(undefined);
  const searchPlaceholder = (() => {
    if (searchingKind === "org") {
      return "Search for an organization...";
    } else if (searchingKind === "job") {
      return "Search for a job...";
    } else if (searchingKind === "access") {
      return "Search for an organization access...";
    }
    return "Search...";
  })();

  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const navigatorItems = useNavigatorItems(searchingKind)!;
  const results: List<NavigatorItem> = navigatorItems.filter((item) =>
    searchingKind === "access" ? item : item.label.toLowerCase().includes(query.toLowerCase()),
  );

  // Selectors
  const userOrgId = useAppSelector(currentUserOrgId);
  const userId = useAppSelector(currentUserId);
  const currentOrgId = useAppSelector(currentOrgIdSelector);
  const currentJobId = useAppSelector(currentJobIdSelector);
  const isLoading = useAppSelector(
    (state) =>
      loading(state, { modelName: "orgOverviews" }) ||
      loading(state, { modelName: "jobOverviews" }) ||
      loading(state, { modelName: "accesses" }),
  );

  // Constants
  const dvhSupported = CSS.supports("(height: 100dvh)");

  // Methods
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
  };

  const highlightQuery = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const replaceParams = (link: string) => {
    let replacedLink = link;
    if (link.includes(":currentUserOrgId")) {
      replacedLink = replacedLink.replace(":currentUserOrgId", userOrgId?.toString() || "");
    }
    if (link.includes(":currentOrgId")) {
      replacedLink = replacedLink.replace(":currentOrgId", currentOrgId?.toString() || "");
    }
    if (link.includes(":currentJobId")) {
      replacedLink = replacedLink.replace(":currentJobId", currentJobId?.toString() || "");
    }
    return replacedLink;
  };

  const handleSelectResult = async (index: number) => {
    const selectedResult = results.get(index);
    if (!selectedResult) return;
    if (
      selectedResult.kind === "go-to" ||
      selectedResult.kind === "go-to-job" ||
      selectedResult.kind === "go-to-org" ||
      selectedResult.kind === "go-to-setting"
    ) {
      navigate(replaceParams(selectedResult.link));
      close();
    } else if (selectedResult.kind === "go-to-search") {
      setSearchingKind(selectedResult.searchKind);
      setQuery("");
    } else if (selectedResult.kind === "select-access") {
      dispatch(UserActions.removeCurrentUser());
      dispatch(JobActions.receiveJobParams({}));
      await dispatch(AuthAsyncActions.changeAccessUid(undefined, selectedResult.accessId));
      navigate("/jobs");
      close();
    } else if (selectedResult.kind === "action") {
      selectedResult.action();
      close();
    }
  };

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "ArrowDown") {
        event.preventDefault();

        setSelectedIndex(results.size - 1);
        document.getElementById(`result-${results.size - 1}`)?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } else if ((event.metaKey || event.ctrlKey) && event.key === "ArrowUp") {
        event.preventDefault();

        setSelectedIndex(0);
        document.getElementById(`result-${0}`)?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } else if (event.key === "ArrowDown") {
        event.preventDefault();

        setSelectedIndex((prev) => Math.min(prev + 1, results.size - 1));
        document.getElementById(`result-${selectedIndex + 1}`)?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();

        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        document.getElementById(`result-${selectedIndex - 1}`)?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } else if (event.key === "Enter") {
        event.preventDefault();

        handleSelectResult(selectedIndex);
      }
    },
    [results, selectedIndex],
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query, userId, searchingKind) => {
        if (searchingKind === "org") {
          dispatch(
            OrgAsyncActions.listOrgs(
              orgParamsFromJSON({
                query,
              }),
            ),
          );
        }

        if (searchingKind === "access" && userId) {
          dispatch(
            AccessAsyncActions.listAccesses(
              accessParamsFromJSON({
                org_name: query,
                userId,
              }),
            ),
          );
        }

        if (searchingKind === "job") {
          dispatch(
            JobAsyncActions.listJobs(
              jobParamsFromJSON({
                query,
              }),
            ),
          );
        }
      }, 500),
    [dispatch],
  );

  // Lifecycle
  React.useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  React.useEffect(() => {
    debouncedSearch(query, userId, searchingKind);
  }, [query, userId, searchingKind]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-[99]">
      <div className="bg-white rounded-xl shadow-lg w-1/2 z-[100] ">
        <LoadingBar loading={isLoading} />
        <div className="p-4">
          <div className="relative">
            <input
              type="text"
              id="navigator-search"
              autoComplete="off"
              placeholder={searchPlaceholder}
              className={`w-full p-3 text-lg bg-gray-50 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all placeholder-gray-400 ${searchingKind ? "pl-[80px]" : ""}`}
              autoFocus
              value={query}
              onChange={handleInputChange}
            />
            {searchingKind && (
              <span className="absolute top-3 left-3 mr-3 px-2 py-1 text-sm font-semibold text-green-700 bg-green-100 rounded">
                Search
              </span>
            )}
          </div>
        </div>
        <div
          className={`p-4 ${dvhSupported ? "max-h-[80dvh]" : "max-h-[80vh]"} overflow-y-auto border-t border-secondary-100`}>
          <ul>
            {results.map((result, index) => {
              if (result.kind === "select-access") {
                return (
                  <AccessResult
                    key={index}
                    accessId={result.accessId}
                    selectedIndex={selectedIndex}
                    close={close}
                    query={query}
                    index={index}
                  />
                );
              } else
                return (
                  <li
                    key={index}
                    onClick={() => handleSelectResult(index)}
                    id={`result-${index}`}
                    className={`p-2 cursor-pointer flex items-center ${
                      selectedIndex === index ? "bg-primary-100" : "hover:bg-gray-100"
                    }`}>
                    {result.kind === "go-to" && (
                      <span className="mr-3 px-2 py-1 text-sm font-semibold text-blue-700 bg-blue-100 rounded">
                        Go to
                      </span>
                    )}
                    {result.kind === "go-to-job" && (
                      <span className="mr-3 px-2 py-1 text-sm font-semibold text-yellow-700 bg-yellow-100 rounded">
                        Go to Job
                      </span>
                    )}
                    {result.kind === "go-to-search" && (
                      <span className="mr-3 px-2 py-1 text-sm font-semibold text-green-700 bg-green-100 rounded">
                        Search
                      </span>
                    )}
                    {result.kind === "go-to-org" && <OrgItemLabel item={result} />}
                    {result.kind === "action" && (
                      <span className="mr-3 px-2 py-1 text-sm font-semibold text-red-700 bg-red-100 rounded">
                        Action
                      </span>
                    )}
                    {result.kind === "go-to-setting" && (
                      <>
                        <span className="mr-1 px-2 py-1 text-sm font-semibold text-blue-700 bg-blue-100 rounded">
                          Go to
                        </span>
                        <span className="mr-3 px-2 py-1 text-sm font-semibold text-purple-700 bg-purple-100 rounded">
                          Setting
                        </span>
                      </>
                    )}
                    <span>{highlightQuery(result.label, query)}</span>
                  </li>
                );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};
